import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SummaryApi from "../common";
import logo from "../asset/logo.png";

// Helper function to safely parse the college_id from the complex localStorage string
const parseLocalStorage = (tokenString) => {
  if (typeof tokenString !== 'string' || !tokenString) return null;
  
  // Look for the pattern college_id[number]
  const match = tokenString.match(/college_id(\d+)/);

  if (match && match[1] !== undefined) {
    return Number(match[1]);
  }
  return null;
};

const StudentCourseSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedCategoryId = location?.state?.selectedCategoryId;

  // --- START: CUSTOM LOGIC FOR PRICING ---
  // WARNING: Replace 'YOUR_AUTH_TOKEN_KEY' with the actual localStorage key in a real application.
  // For this exercise, we use the hardcoded string provided in the previous prompt 
  // to demonstrate the required logic structure.
  
  // In a real app, use: 
  // const studentTokenString = localStorage.getItem('YOUR_AUTH_TOKEN_KEY'); 
  const studentTokenString = "H7Z4L9VD2XK5JW8QRRS7temp_token_4dn4wd2lr56academic_coursedegreecollege_id1student_emailjmartin@rodriguez.comstudent_id6themelight"; // Hardcoded token for college_id extraction demo

  const collegeId = parseLocalStorage(studentTokenString);
  // Price is FREE if collegeId is NOT 0
  const isCollegePriceApplied = collegeId !== 0; 
  // --- END: CUSTOM LOGIC FOR PRICING ---

  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");

  const formatImageUrl = (imagePath) => {
    if (!imagePath) return "";
    const baseUrl = SummaryApi.Images.url.replace("/api", "");
    if (imagePath.startsWith("http")) return imagePath;
    return `${baseUrl}/${imagePath}`;
  };

  // Redirect if no category
  useEffect(() => {
    if (!selectedCategoryId) {
      navigate("/student-panel/category-selection");
      return;
    }

    const fetchCourses = async () => {
      try {
        const res = await fetch(SummaryApi.coursesByCategoryId.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ category_id: selectedCategoryId }),
        });
        const result = await res.json();
        if (result.status && Array.isArray(result.data)) {
          setCourses(result.data);
        } else {
          console.error("Unexpected API response:", result);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };

    fetchCourses();
  }, [selectedCategoryId, navigate]);

  // Load saved course selection
  useEffect(() => {
    const savedCourseId = sessionStorage.getItem("selectedCourseId");
    if (savedCourseId) setSelectedCourseId(savedCourseId);
  }, []);

  const handleCourseChange = (course) => {
    const id = String(course.course_id);
    setSelectedCourseId(id);
    sessionStorage.setItem("selectedCourseId", id);
  };

  return (
    <div className="relative flex flex-col items-center justify-center w-full min-h-screen bg-white font-inter text-white">

      {/* Logo */}
      <div className="w-full flex justify-center mt-6 mb-4">
        <img src={logo} alt="logo" className="w-40 sm:w-48 md:w-56 lg:w-64 object-contain" />
      </div>

  <div
    className="
      relative z-10 bg-[#1C3664] shadow-2xl rounded-2xl
      w-[80%] mt-6 mb-16 px-6 sm:px-8 md:px-10 lg:px-12 py-20 
      overflow-hidden
    "
  >
        {/* Header */}
        <div className="text-center mb-8 text-white">
          <h2 className="text-[32px] sm:text-[36px] md:text-[38px] font-bold mb-2">
            COURSES
            <span className="block w-16 h-[3px] bg-white mx-auto mt-1 relative">
              <span className="block w-2 h-2 bg-white rotate-45 absolute -bottom-1 left-1/2 -translate-x-1/2"></span>
            </span>
          </h2>
          <h3 className="text-[#e69818] text-[20px] sm:text-[22px] font-bold mb-2">
            Choose Your Learning Path
          </h3>
          <p className="text-[16px] sm:text-[17px] opacity-90 text-white">
            Select the course that interests you the most
          </p>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
          {courses.length > 0 ? courses.map((course) => (
            <label
              key={course.course_id}
              className={`
                cursor-pointer p-6 rounded-xl shadow-md border flex flex-col
                items-center justify-center transition-all duration-300 bg-white
              ${selectedCourseId === String(course.course_id)
                  ? "scale-105 border-2 border-[#e69818] shadow-xl"
                  : "border border-transparent hover:border-[#e69818] hover:shadow-lg"
                }

              `}
            >
              <input
                type="radio"
                name="course"
                value={course.course_id}
                checked={selectedCourseId === String(course.course_id)}
                onChange={() => handleCourseChange(course)}
                className="hidden"
              />

              <img
                src={formatImageUrl(course.course_thumbnail || course.course_image1)}
                alt={course.course_title}
                className="w-20 h-20 object-cover rounded-md mb-4"
              />

              <h3 className="text-lg font-semibold mb-1 text-[#1C3664] text-center">
                {course.course_title}
              </h3>

              <p className="text-sm opacity-80 text-[#1C3664] mb-1">
                Category: {course.category_name || "N/A"}
              </p>

              <div className="flex items-center mb-1">
                {Array.from({ length: 5 }, (_, i) => (
                  <svg
                    key={i}
                    className={`w-4 h-4 ${i < Math.round(course.rating) ? "text-yellow-400" : "text-gray-300"}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.148c.969 0 1.371 1.24.588 1.81l-3.36 2.44a1 1 0 00-.364 1.118l1.286 3.957c.3.921-.755 1.688-1.54 1.118l-3.36-2.44a1 1 0 00-1.176 0l-3.36 2.44c-.784.57-1.838-.197-1.539-1.118l1.285-3.957a1 1 0 00-.364-1.118L2.027 9.384c-.783-.57-.38-1.81.588-1.81h4.148a1 1 0 00.95-.69l1.286-3.957z" />
                  </svg>
                ))}
                <span className="text-gray-600 text-sm ml-2">{course.rating}</span>
              </div>

              {/* START: UPDATED PRICE LOGIC */}
              {isCollegePriceApplied ? (
                // Scenario: college_id is NOT 0 (e.g., college_id1), meaning course is FREE
                <div className="flex flex-col items-center">
                  <p className="text-gray-400 font-bold text-base line-through">
                    ₹{parseFloat(course.total_price).toLocaleString()}
                  </p>
                  <p className="text-green-600 font-bold text-xl mt-1">
                    FREE
                  </p>
                </div>
              ) : (
                // Scenario: college_id IS 0 (Course is NOT free)
                <p className="text-[#1C3664] font-bold text-lg">
                  ₹{parseFloat(course.total_price).toLocaleString()}
                </p>
              )}
              {/* END: UPDATED PRICE LOGIC */}

            </label>
          )) : (
            <p className="text-white col-span-full text-center">No courses found for this category.</p>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="w-full mt-12 flex justify-between px-2">
          <button
            type="button"
            className="border border-white text-white rounded-md py-3 px-6 text-lg font-medium hover:bg-white hover:text-[#1C3664] transition shadow-md"
            onClick={() => navigate("/student-panel/category-selection")}
          >
            &lt; Back
          </button>

          <button
            type="button"
            className={`
              rounded-md py-3 px-8 text-lg font-medium shadow-md transition
              ${selectedCourseId ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-gray-400 text-white cursor-not-allowed"}
            `}
            disabled={!selectedCourseId}
            onClick={() => navigate("/student-panel/instructor-selection", { state: { selectedCourseId } })}
          >
            Next &gt;
          </button>
        </div>

      </div>
    </div>
  );
};

export default StudentCourseSelection;